import { Link } from "react-router-dom";
import { ArrowRight, Check, Send, ShieldCheck } from "lucide-react";
import { getButtonClassName } from "../components/Button";
import { Card } from "../components/Card";
import { Chip } from "../components/Chip";
import { ReadinessDial } from "../components/ReadinessDial";
import { StatusBadge } from "../components/StatusBadge";

const capabilities = [
  {
    label: "Stays private",
    detail: "Your job details stay on your device until you choose to send them.",
    icon: ShieldCheck,
  },
  {
    label: "No lead fees",
    detail: "Pros never pay to see your job. No subscriptions, bidding wars, or fake urgency.",
    icon: Check,
  },
  {
    label: "Easy to send",
    detail: "Get a clean job brief you can email, text, or print for any local pro.",
    icon: Send,
  },
];

const briefFacts = [
  ["Work", "Clear and flush gutters", "Confirmed"],
  ["Size", "60 linear feet", "Confirmed"],
  ["Access", "Single-story ranch", "Confirmed"],
  ["Weather", "Needs answer", "Needs answer"],
];

const heroSignals = [
  ["Your data", "Stays on your device"],
  ["Lead fees", "$0, ever"],
  ["Send to a pro", "Email or text"],
];

const steps = [
  [
    "01",
    "Describe the job",
    "Tell us what needs done in plain English. ClearScope helps fill in the details.",
  ],
  [
    "02",
    "See what's missing",
    "We highlight gaps and flag work that may need a licensed pro before you send.",
  ],
  [
    "03",
    "Send it to a pro",
    "Email or text the job brief to any contractor. They do not need an account.",
  ],
];

function TrustItem({ children }: { children: string }) {
  return (
    <span className="inline-flex items-center gap-2 text-sm font-semibold text-text-muted">
      <Check aria-hidden="true" className="h-4 w-4 text-accent-700" strokeWidth={1.5} />
      {children}
    </span>
  );
}

function JobBriefPreview() {
  return (
    <div className="relative mx-auto w-full max-w-[34rem]">
      <div className="absolute -inset-8 rounded-[32px] bg-[var(--gradient-radial-accent)] opacity-80 blur-2xl" />
      <div className="relative overflow-hidden rounded-[24px] border border-border bg-surface/86 shadow-[var(--shadow-3)] backdrop-blur-xl lg:-rotate-1">
        <div className="flex h-11 items-center gap-2 border-b border-border bg-surface-raised px-4">
          <span className="h-2.5 w-2.5 rounded-pill bg-danger" />
          <span className="h-2.5 w-2.5 rounded-pill bg-warning" />
          <span className="h-2.5 w-2.5 rounded-pill bg-success" />
          <span className="ml-2 rounded-pill border border-border bg-paper px-3 py-1 text-xs font-semibold text-text-subtle">
            clearscope.com/job-brief
          </span>
        </div>
        <Card className="m-3 scope-preview overflow-hidden" padding="lg" variant="hero">
          <div className="flex items-start justify-between gap-5">
            <div>
              <p className="page-eyebrow">Your job brief</p>
              <p className="mt-5 font-sans text-3xl font-semibold leading-none text-text-strong">
                60 linear feet
              </p>
              <p className="mt-2 text-sm font-semibold text-text-muted">Gutters - West End</p>
            </div>
            <ReadinessDial
              caption="4 of 5 details"
              glow
              label="Detail"
              level="AMBER"
              score={78}
              secondaryRing
              size="xl"
            />
          </div>

          <div className="my-7 h-px bg-border" />

          <div className="grid gap-0">
            {briefFacts.map(([label, value, state]) => (
              <div
                key={label}
                className="grid grid-cols-[6rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-border py-3 last:border-b-0"
              >
                <p className="cell-label">{label}</p>
                <p className="text-sm font-semibold text-text">{value}</p>
                <Chip tone={state === "Confirmed" ? "success" : "warning"}>{state}</Chip>
              </div>
            ))}
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-between gap-3 rounded-card border border-warning-border bg-warning-bg/70 px-4 py-3">
            <Chip tone="warning">Almost ready</Chip>
            <StatusBadge dotPosition="trailing" label="Detail 78" level="AMBER" tone="glyph" />
          </div>
        </Card>
      </div>
    </div>
  );
}

function MiniJobBriefPreview() {
  return (
    <Card className="mt-7 sm:hidden" padding="sm" variant="glass">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="page-eyebrow">Your job brief</p>
          <p className="mt-2 text-lg font-semibold leading-tight text-text-strong">
            Gutters - West End
          </p>
          <p className="mt-2 text-sm font-semibold text-text-muted">
            60 linear feet - Almost ready
          </p>
        </div>
        <ReadinessDial caption="4 of 5" label="Detail" level="AMBER" score={78} size="sm" />
      </div>
    </Card>
  );
}

export function LandingPage() {
  return (
    <div className="stack-section pb-12">
      <section className="hero-paper page-narrow mx-auto grid items-center gap-12 overflow-hidden rounded-[28px] border border-border p-6 shadow-[var(--shadow-3)] md:p-8 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] lg:px-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="page-kicker">Free - No signup - No lead fees</div>
          <h1 className="display mt-6 font-semibold">Small jobs, done right.</h1>
          <p className="lede mt-5 max-w-2xl">
            Write what you need in plain words. ClearScope turns it into a clean, complete job brief
            you can send to any handyman or contractor in under two minutes.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link className={getButtonClassName("primary", "", "lg")} to="/post">
              Post a job
              <ArrowRight aria-hidden="true" className="h-4 w-4" />
            </Link>
            <Link className={getButtonClassName("secondary", "", "lg")} to="/jobs">
              Find work near me
            </Link>
          </div>
          <Link className="text-link mt-5 inline-flex text-sm font-semibold" to="/about">
            See how it works
          </Link>
          <MiniJobBriefPreview />
          <div className="mt-7 hidden max-w-2xl gap-3 sm:grid sm:grid-cols-3">
            {heroSignals.map(([label, value]) => (
              <div key={label} className="stat-glass px-4 py-3">
                <p className="cell-label">{label}</p>
                <p className="mt-1 text-sm font-semibold text-text">{value}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 hidden flex-wrap gap-x-5 gap-y-3 rounded-card border border-border bg-surface-raised/80 px-4 py-3 shadow-[var(--shadow-card)] sm:flex">
            <TrustItem>No spam calls</TrustItem>
            <TrustItem>No tracking</TrustItem>
            <TrustItem>No upsells</TrustItem>
          </div>
        </div>
        <div className="hidden sm:block">
          <JobBriefPreview />
        </div>
      </section>

      <section className="page-narrow mx-auto grid gap-4 md:grid-cols-3">
        {capabilities.map((item) => (
          <Card key={item.label} interactive padding="lg" variant="glass">
            <div className="flex h-12 w-12 items-center justify-center rounded-[16px] bg-[var(--gradient-brand-soft)] text-accent-700 shadow-[var(--shadow-card)]">
              <item.icon aria-hidden="true" className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <h2 className="card-title mt-5">{item.label}</h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">{item.detail}</p>
          </Card>
        ))}
      </section>

      <section className="page-narrow mx-auto grid gap-0 overflow-hidden rounded-card border border-border bg-paper shadow-[var(--shadow-key-2)] md:grid-cols-3">
        {steps.map(([step, title, body], index) => (
          <article
            key={step}
            className="relative border-b border-border p-6 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
          >
            <p className="font-mono text-xs font-semibold tracking-normal text-text-subtle num">
              {step}
            </p>
            <h2 className="card-title mt-4">{title}</h2>
            <p className="mt-3 text-sm leading-6 text-text-muted">{body}</p>
            {index < steps.length - 1 ? (
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-6 right-6 h-px bg-border md:bottom-auto md:left-auto md:right-0 md:top-6 md:h-[calc(100%-3rem)] md:w-px"
              />
            ) : null}
          </article>
        ))}
      </section>

      <section className="page-narrow mx-auto grid gap-5 lg:grid-cols-[0.92fr_1.08fr]">
        <Card className="grid gap-4" padding="lg" variant="surface">
          <div>
            <p className="page-kicker">See it in action</p>
            <h2 className="section-title mt-4 font-semibold">
              From sticky note to clean quote request
            </h2>
          </div>
          <blockquote className="border-l-2 border-border pl-5 text-[1.0625rem] leading-7 text-text-muted">
            Need gutters done. Back side is high. Ranch house. Sometime this week. Leaves and pine
            needles. Need someone with ladder.
          </blockquote>
        </Card>
        <Card className="grid gap-5" padding="lg" variant="surface">
          <div className="grid gap-0 rounded-card border border-border bg-surface-raised">
            {[
              ["Work", "Clear and flush gutters"],
              ["Watch", "Heights - ladder access required"],
              ["Detail", "78/100"],
            ].map(([label, value]) => (
              <div
                key={label}
                className="grid grid-cols-[7rem_1fr] gap-3 border-b border-border px-4 py-3 last:border-b-0"
              >
                <p className="cell-label">{label}</p>
                <p className="text-sm font-semibold text-text">{value}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 rounded-card border border-border bg-surface-raised p-4">
            <ReadinessDial glow label="Detail" level="AMBER" score={78} size="md" />
            <p className="text-sm leading-6 text-text-muted">
              The detail score helps you spot missing info before you send. It is not a contractor
              rating.
            </p>
          </div>
        </Card>
      </section>
    </div>
  );
}
